/**
 * #605 — Papan's picker as a POPUP, for a host whose API client cannot ask as
 * the person: the share-view host (it acts as the share). The same flow the
 * SDK runs itself on pd.app:
 *   1. open the popup BLANK inside the app's click (a popup opened after a
 *      round trip is blocked);
 *   2. register the request through the app's OWN serve session
 *      (`/s/<session>/_papan/handoff/requests`, credential-less — the session
 *      id is the capability), so the answer is bound to that session; the
 *      server answers with the request's id — never one chosen here, which a
 *      caller could register again and again behind a single link — and the
 *      popup is pointed at the picker for it;
 *   3. long-poll the server for the person's answer — the popup signs them in
 *      if needed and answers first-party, never through this page.
 * Only step 1 is synchronous; call `askByPopup` before any `await`.
 */
import type { HandoffCapRefusal, HandoffOutcome } from '@papan/types'

export type PopupAsk =
  | { kind: 'outcome'; outcome: HandoffOutcome }
  | { kind: 'refused'; refusal: HandoffCapRefusal }
  | { kind: 'ended'; status: 'popup_blocked' | 'unavailable' | 'cancelled' }

export function askByPopup(args: {
  /** The app's serve session (`<origin>/s/<sessionId>`). */
  serve: { origin: string; sessionId: string }
  /** What to ask: the request's kind and the app's options (and a save's size). */
  begin: Record<string, unknown>
  /** Give up: the app's AbortSignal fired, or the viewer moved on. */
  signal: AbortSignal
  /** The popup, once open — the relay watches whether it is still there. */
  onOpen?: (popup: Window) => void
}): Promise<PopupAsk> {
  // Synchronous, inside the app's click (user activation reaches this frame).
  // A window of its own: a save's popup stays open following its upload, and
  // the next request must not navigate it away from telling the person.
  const popup = window.open('', newWindowName(), 'popup,width=860,height=640')
  if (!popup) return Promise.resolve({ kind: 'ended', status: 'popup_blocked' })
  args.onOpen?.(popup)
  return follow(popup, args)
}

/**
 * Is the popup gone? `closed` is only a hint: a popup that passes through a
 * page sending `Cross-Origin-Opener-Policy` (a sign-in's redirect) reads as
 * closed to this page while the person is still in it. So it never ANSWERS a
 * request — it only frees what the ask holds (the relay: a fresh ask may take
 * the place of one whose popup looks gone).
 */
export function popupClosed(popup: Window): boolean {
  try { return popup.closed === true } catch { return false }
}

async function follow(
  popup: Window,
  { serve, begin, signal }: { serve: { origin: string; sessionId: string }; begin: Record<string, unknown>; signal: AbortSignal },
): Promise<PopupAsk> {
  const base = `${serve.origin}/s/${encodeURIComponent(serve.sessionId)}/_papan/handoff/requests`
  const closePopup = () => { try { popup.close() } catch { /* already gone */ } }

  const giveUp = (pid?: string) => {
    if (pid) void fetch(`${base}/${pid}`, { method: 'DELETE', credentials: 'omit', keepalive: true }).catch(() => {})
    closePopup()
  }

  // Bounded, and ended by the ask's signal: a begin that never answers must
  // not hold the page's one picker.
  const beginCall = bounded(BEGIN_TIMEOUT_MS, signal)
  // Before the begin: the server's clock on the request starts during it, so
  // an expiry is never misread as the session ending (see the 404 below).
  const begunAt = Date.now()
  const begun = await fetch(base, {
    method: 'POST',
    credentials: 'omit',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(begin),
    signal: beginCall.signal,
  }).catch(() => null)
  beginCall.done()
  if (!begun?.ok && beginCall.signal.aborted) {
    // Cut short. A request the server registered all the same was never
    // shown: no one holds its id, and it expires unanswered.
    giveUp()
    return { kind: 'ended', status: signal.aborted ? 'cancelled' : 'unavailable' }
  }
  if (!begun?.ok) {
    closePopup()
    // Over Papan's ceiling: nothing to ask the person — say by how much.
    const refusal = begun?.status === 413
      ? ((await begun.json().catch(() => null)) as { refusal?: HandoffCapRefusal } | null)?.refusal
      : undefined
    return refusal ? { kind: 'refused', refusal } : { kind: 'ended', status: 'unavailable' }
  }

  const issued = ((await begun.json().catch(() => null)) as { pid?: unknown } | null)?.pid
  if (typeof issued !== 'string' || !REQUEST_ID.test(issued)) { closePopup(); return { kind: 'ended', status: 'unavailable' } }
  const pid = issued
  if (signal.aborted) { giveUp(pid); return { kind: 'ended', status: 'cancelled' } }
  // The person closed the blank window before the picker could load in it.
  if (popupClosed(popup)) { giveUp(pid); return { kind: 'ended', status: 'cancelled' } }
  try {
    popup.location.replace(`${window.location.origin}/__handoff/${pid}`)
  } catch {
    giveUp(pid)
    return { kind: 'ended', status: 'unavailable' }
  }

  // Given up (the app's signal, the viewer moving on, a fresh ask taking this
  // one's place): settle the request as closed — nothing changes if the
  // person answered in the same instant — and read how it really ended, so
  // an answer that stands can be voided rather than left live for no one.
  const settleGivenUp = async (): Promise<PopupAsk> => {
    closePopup()
    await fetch(`${base}/${pid}`, { method: 'DELETE', credentials: 'omit', keepalive: true, signal: timeout(SETTLE_MS) }).catch(() => null)
    const res = await fetch(`${base}/${pid}`, { credentials: 'omit', signal: timeout(SETTLE_MS) }).catch(() => null)
    const outcome = res?.ok ? await res.json().catch(() => null) as HandoffOutcome | null : null
    return outcome && outcome.status === 'answered' ? { kind: 'outcome', outcome } : { kind: 'ended', status: 'cancelled' }
  }

  // Past the request's life on the server no answer is coming: stop, and free
  // the page's one picker — whatever the server has been answering meanwhile.
  const waitUntil = begunAt + REQUEST_LIFETIME_MS
  for (;;) {
    if (signal.aborted) return settleGivenUp()
    if (Date.now() > waitUntil) { giveUp(pid); return { kind: 'ended', status: 'cancelled' } }
    // Each call waits server-side (~15s) for the person, then says
    // "pending" — bounded on its own: one stalled response must not hold the
    // page's one picker until the request's life runs out.
    const poll = bounded(POLL_TIMEOUT_MS, signal)
    const res = await fetch(`${base}/${pid}`, { credentials: 'omit', signal: poll.signal }).catch(() => null)
    const outcome = res?.ok ? await res.json().catch(() => null) as HandoffOutcome | null : null
    poll.done()
    if (signal.aborted) return settleGivenUp()
    // Gone after it began. Past its life on the server, it expired unanswered —
    // the person walked away. Sooner, the share session behind it ended
    // (revoked, expired): nothing can land now, so the picker must not stay
    // open to be answered into nothing — and the app hears it as a failure.
    if (res?.status === 404) {
      closePopup()
      const expired = Date.now() - begunAt >= REQUEST_TTL_MS - 5_000
      return { kind: 'ended', status: expired ? 'cancelled' : 'unavailable' }
    }
    // Not an answer at all (lost, stalled, a misrouted page): never spin on it.
    if (!outcome) { await pause(1_000, signal); continue }
    if (outcome.status === 'pending') continue
    return { kind: 'outcome', outcome }
  }
}

/** How long settling a given-up ask may take — it holds the page's picker. */
const SETTLE_MS = 5_000

function timeout(ms: number): AbortSignal {
  const controller = new AbortController()
  setTimeout(() => controller.abort(), ms)
  return controller.signal
}

/** Aborts when `signal` does, or after `ms` — one call bounded on its own.
 *  `done()` lets go of both once the call has settled. */
function bounded(ms: number, signal: AbortSignal): { signal: AbortSignal; done: () => void } {
  const controller = new AbortController()
  const stop = () => controller.abort()
  const timer = setTimeout(stop, ms)
  signal.addEventListener('abort', stop, { once: true })
  return { signal: controller.signal, done: () => { clearTimeout(timer); signal.removeEventListener('abort', stop) } }
}

/** How long one long-poll may take: the server holds it ~15s. */
const POLL_TIMEOUT_MS = 25_000

/** How long registering the request may take before the ask gives up — the
 *  popup stays blank meanwhile. */
const BEGIN_TIMEOUT_MS = 10_000

/** How long the server keeps an unanswered request (`REQUEST_TTL_MS`, 10
 *  min), with a margin — as the SDK's pd.app host waits. */
const REQUEST_LIFETIME_MS = 11 * 60_000

/** The server's `REQUEST_TTL_MS`: a request 404s sooner only when the session behind it is gone. */
const REQUEST_TTL_MS = 10 * 60_000

/** A request id as the server mints it (256 bits, base64url). */
const REQUEST_ID = /^[A-Za-z0-9_-]{43}$/

/** A fresh window name per ask: a save's popup stays open following its upload. */
function newWindowName(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(9))
  return `papan-handoff-${Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')}`
}

function pause(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    const timer = setTimeout(done, ms)
    function done() {
      clearTimeout(timer)
      signal.removeEventListener('abort', done)
      resolve()
    }
    signal.addEventListener('abort', done, { once: true })
  })
}
